import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import StarRating from './StarRating';
import StateBlock from './StateBlock';
import { apiUrl, mediaUrl } from '../../config/api';
import './ReviewShowcase.scss';

/**
 * Normalises both review response shapes: the best-review endpoints
 * return `reviews` as a plain array, while `/api/fetch-all-reviews` is
 * paginated (`reviews.data` plus `next_page_url`).
 */
const parseReviews = (data) => {
  const payload = data?.reviews;
  if (Array.isArray(payload)) return { items: payload, nextPage: null };
  return {
    items: Array.isArray(payload?.data) ? payload.data : [],
    nextPage: payload?.next_page_url ? payload.current_page + 1 : null,
  };
};

const withPage = (endpoint, page) =>
  page > 1 ? `${endpoint}${endpoint.includes('?') ? '&' : '?'}page=${page}` : endpoint;

/**
 * Customer review gallery, shared by the landing-page rail
 * (`/api/fetch-best-reviews`) and the full page
 * (`/api/fetch-all-reviews`), which were two copies of the same
 * component differing only in that endpoint.
 */
const ReviewShowcase = ({
  endpoint,
  layout = 'rail',
  // Pins the grid to a fixed column count instead of auto-filling.
  columns,
  // Caps how many reviews render, for the landing-page section.
  limit,
  skeletonCount = 4,
}) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextPage, setNextPage] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);

  const fetchReviews = useCallback(
    async (signal) => {
      setLoading(true);
      try {
        const response = await fetch(apiUrl(endpoint), { signal });
        if (!response.ok) throw new Error('Failed to fetch reviews');
        const { items, nextPage: next } = parseReviews(await response.json());
        setReviews(items);
        setNextPage(next);
        setError(null);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [endpoint],
  );

  const loadMore = async () => {
    if (!nextPage || loadingMore) return;
    setLoadingMore(true);
    setLoadMoreError(false);
    try {
      const response = await fetch(apiUrl(withPage(endpoint, nextPage)));
      if (!response.ok) throw new Error('Failed to fetch reviews');
      const { items, nextPage: next } = parseReviews(await response.json());
      setReviews((current) => [...current, ...items]);
      setNextPage(next);
    } catch {
      setLoadMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchReviews(controller.signal);
    return () => controller.abort();
  }, [fetchReviews]);

  const containerClass =
    layout === 'rail'
      ? 'review-rail'
      : `review-showcase-grid${columns ? ` review-showcase-grid--${columns}up` : ''}`;

  const visibleReviews = limit ? reviews.slice(0, limit) : reviews;

  if (loading) {
    return (
      <div className={containerClass} aria-busy="true">
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <div className="review-tile" key={index} aria-hidden="true">
            <div className="skeleton review-tile__media" />
            <div className="review-tile__body">
              <div className="skeleton skeleton--text" style={{ width: '40%' }} />
              <div className="skeleton skeleton--text" />
              <div className="skeleton skeleton--text" style={{ width: '70%' }} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <StateBlock
        variant="error"
        title="Reviews are unavailable"
        message="We couldn't load customer reviews right now."
        actionLabel="Retry"
        onAction={() => fetchReviews()}
      />
    );
  }

  if (visibleReviews.length === 0) {
    return (
      <StateBlock
        title="No reviews yet"
        message="Once customers start sharing their photos, they will appear here."
      />
    );
  }

  const showLoadMore = !limit && nextPage;

  return (
    <>
      <div className={containerClass}>
        {visibleReviews.map((review) => {
          // Prefer the customer's own photo; fall back to the product shot.
          const image =
            mediaUrl(review.review_image_1) || mediaUrl(review?.product?.product_image_1);
          // `/api/fetch-all-reviews` has no nested product; its
          // `product_id` already holds the product code.
          const productCode = review?.product?.product_code ?? review?.product_id;
          const author = review.user_name ?? review?.user?.name;

          return (
            <article className="review-tile" key={review.id}>
              {image ? (
                <img
                  className="review-tile__media"
                  src={image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="review-tile__media" aria-hidden="true" />
              )}

              <div className="review-tile__body">
                <StarRating value={review.rating} size="sm" />
                <p className="review-tile__text">{review.review}</p>
                {author && <p className="review-tile__author">— {author}</p>}

                {productCode && (
                  <Link
                    className="btn btn--outline btn--sm btn--block"
                    to={`/productshow/${productCode}`}
                  >
                    Shop this piece
                  </Link>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {showLoadMore && (
        <div className="review-showcase__more">
          <button
            type="button"
            className="btn btn--outline"
            onClick={loadMore}
            disabled={loadingMore}
            aria-busy={loadingMore}
          >
            {loadingMore ? 'Loading…' : loadMoreError ? 'Retry loading more' : 'Load more reviews'}
          </button>
        </div>
      )}
    </>
  );
};

export default ReviewShowcase;
